import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  ChevronDownIcon,
  CircleCheckIcon,
  CircleXIcon,
  Columns3Icon,
  InboxIcon,
  ListIcon,
  NetworkIcon,
  PlusIcon,
  SearchIcon,
  SendIcon,
  TicketIcon,
  UserIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input, Select as NativeSelect } from '@/components/ui/input';
import { PageSpinner } from '@/components/ui/misc';
import { usePermissions } from '@/auth/use-permissions';
import { useAuthUser } from '@/auth/use-auth';
import { PreticketsPanel } from '@/features/pretickets/PreticketsPanel';
import { pendingPreticketsCountQuery } from '@/features/pretickets/pretickets-queries';
import { cn } from '@/lib/utils';
import {
  creatableDesks,
  desksListQuery,
  selectableTicketGroups,
  ticketGroupsQuery,
  ticketsListQuery,
  useOperatedClientIds,
} from './tickets-queries';
import { EmptyData } from './ticket-chips';
import { TicketBoard } from './TicketBoard';
import { TicketList } from './TicketList';
import { NewTicketDialog } from './NewTicketDialog';
import { rememberTicketsList } from './tickets-back';

export const ticketsSearchSchema = z.object({
  desk: z.coerce.number().optional(),
  /** v12: `pretickets` é a triagem de e-mails, que saiu da sidebar e virou
   *  uma view desta página (gated por `pretickets.access`). */
  view: z.enum(['list', 'board', 'pretickets']).default('list'),
  status: z.enum(['open', 'closed', 'canceled', 'all']).default('open'),
  q: z.string().default(''),
  /** v11: filtro por grupo de tickets (ausente = todos). Lixo na URL é
   *  ignorado (catch) em vez de derrubar a lista inteira com 422. */
  group: z.coerce.number().int().positive().optional().catch(undefined),
  /** spec 066: "Meus tickets" (responsável = quem está logado). Só `1` liga. */
  meus: z.coerce.number().pipe(z.literal(1)).optional().catch(undefined),
  /** `?new=1` abre o diálogo de novo ticket (limpo ao fechar). */
  new: z.coerce.number().optional(),
});
export type TicketsSearch = z.infer<typeof ticketsSearchSchema>;

const STATUS_CHIPS = [
  { value: 'open', label: 'Todos abertos', Icon: TicketIcon },
  { value: 'closed', label: 'Fechados', Icon: CircleCheckIcon },
  { value: 'canceled', label: 'Cancelados', Icon: CircleXIcon },
] as const;

export function TicketsPage() {
  const navigate = useNavigate();
  const rawSearch = useSearch({ strict: false });
  const search = useMemo(() => ticketsSearchSchema.parse(rawSearch), [rawSearch]);

  const desksQ = useQuery(desksListQuery());
  const desks = desksQ.data ?? [];
  // spec 011 → 024: há onde abrir ticket? mesa do ERP, ou mesa espelhada com
  // algum cliente operado no ERP
  const operated = useOperatedClientIds();
  const canCreate = creatableDesks(desks, operated, null).length > 0;

  // Pré-tickets (v12): a view só existe para quem tem a key — enquanto o /me
  // não resolveu, ela fica escondida (mesma regra da sidebar: aparecer depois
  // é melhor que aparecer-e-sumir). Quem barra de verdade é a API.
  const { can, ready: permissionsReady } = usePermissions();
  const me = useAuthUser();
  const canPretickets = permissionsReady && can('pretickets.access');
  const isPreticketsView = search.view === 'pretickets';
  const pendingPreticketsQ = useQuery({
    ...pendingPreticketsCountQuery(),
    enabled: canPretickets,
  });
  const pendingPretickets = pendingPreticketsQ.data ?? 0;

  // v11: filtro por grupo de tickets — o select só aparece quando há grupos.
  const groupsQ = useQuery(ticketGroupsQuery());
  const groups = selectableTicketGroups(groupsQ.data ?? [], search.group);

  const [qInput, setQInput] = useState(search.q);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogStageId, setDialogStageId] = useState<number | undefined>(undefined);

  // spec 018: a lista se lembra do próprio estado e da posição no histórico —
  // o "← Tickets" do detalhe volta para ELA, não para o quadro sem filtros.
  // Só enquanto a URL é a da lista: ao abrir um ticket, a location muda ANTES
  // de a lista desmontar, e o índice do detalhe não é o dela.
  const listIndex = useRouterState({
    select: (s) => (s.location.pathname === '/tickets' ? s.location.state.__TSR_index : null),
  });
  useEffect(() => {
    if (listIndex !== null) rememberTicketsList(search, listIndex);
  }, [search, listIndex]);

  const setSearch = (patch: Partial<TicketsSearch>, replace = false) =>
    void navigate({ to: '/tickets', search: { ...search, ...patch }, replace });

  // Busca com debounce (300ms) -> parâmetro `q` na URL
  useEffect(() => {
    if (qInput === search.q) return;
    const t = setTimeout(() => {
      void navigate({ to: '/tickets', search: { ...search, q: qInput }, replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [qInput, search, navigate]);

  // `?view=pretickets` sem a permissão (link antigo, URL colada) volta para a
  // lista em vez de deixar uma view fantasma na URL.
  useEffect(() => {
    if (!isPreticketsView || !permissionsReady || can('pretickets.access')) return;
    void navigate({ to: '/tickets', search: { ...search, view: 'list' }, replace: true });
  }, [isPreticketsView, permissionsReady, can, navigate, search]);

  // Mesa padrão: sem `desk` na URL, escolhe a mesa com MAIS tickets abertos
  // (sonda barata na lista aberta; degrada para a primeira mesa ativa). Na
  // triagem não há lista de tickets na tela — a sonda espera a volta.
  const needsDesk = search.desk === undefined && desks.length > 0 && !isPreticketsView;
  const probeQ = useQuery({
    ...ticketsListQuery({ status: 'open', limit: 200, offset: 0 }),
    enabled: needsDesk,
    staleTime: 60_000,
  });
  useEffect(() => {
    if (!needsDesk) return;
    if (probeQ.isPending && !probeQ.isError) return;
    const counts = new Map<number, number>();
    for (const t of probeQ.data?.items ?? []) {
      counts.set(t.desk.id, (counts.get(t.desk.id) ?? 0) + 1);
    }
    const actives = desks.filter((d) => d.active);
    const pool = actives.length > 0 ? actives : desks;
    let best = pool[0];
    if (!best) return;
    for (const d of pool) {
      if ((counts.get(d.id) ?? 0) > (counts.get(best.id) ?? 0)) best = d;
    }
    setSearch({ desk: best.id }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsDesk, probeQ.isPending, probeQ.isError, probeQ.data, desks]);

  const isDialogOpen = dialogOpen || search.new !== undefined;
  const closeDialog = () => {
    setDialogOpen(false);
    setDialogStageId(undefined);
    if (search.new !== undefined) setSearch({ new: undefined }, true);
  };

  // O kanban exibe sempre os abertos; trocar de chip fora de "abertos" leva à lista.
  const activeStatus = search.view === 'board' ? 'open' : search.status;

  // Props/params da lista — o `key` deriva DESTE objeto (remount ao trocar
  // qualquer filtro), nunca de um template literal montado à mão em paralelo.
  // spec 066: "Meus tickets" filtra pelo responsável = quem está logado
  const responsibleId = search.meus === 1 ? me?.id : undefined;
  const listParams = {
    desk: search.desk,
    status: search.status,
    q: search.q,
    group: search.group,
    responsibleId,
  };

  return (
    <div className="flex min-h-full flex-col gap-3 rounded-lg border border-border/60 bg-card p-4 shadow-sm">
      {/* Linha 1: título + Lista|Kanban + busca + CTA */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold leading-none text-foreground">Tickets</h1>

        <div className="flex items-center gap-0.5 rounded-md bg-muted p-0.5">
          <SegmentButton
            active={search.view === 'list'}
            onClick={() => setSearch({ view: 'list' })}
            Icon={ListIcon}
            label="Lista"
          />
          <SegmentButton
            active={search.view === 'board'}
            onClick={() => setSearch({ view: 'board', status: 'open' })}
            Icon={Columns3Icon}
            label="Kanban"
          />
          {canPretickets ? (
            <SegmentButton
              active={isPreticketsView}
              onClick={() => setSearch({ view: 'pretickets' })}
              Icon={InboxIcon}
              label="Pré-tickets"
              badge={pendingPretickets}
            />
          ) : null}
          {/* spec 025: outra tela (os disparos do WhatsApp), não uma vista desta */}
          <SegmentButton
            active={false}
            onClick={() => void navigate({ to: '/tickets/dispatches' })}
            Icon={SendIcon}
            label="Disparos"
          />
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {isPreticketsView ? null : (
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="Buscar"
                aria-label="Buscar tickets"
                className="h-8 w-56 pl-8 text-[13px]"
              />
            </div>
          )}
          <Button
            size="sm"
            onClick={() => setDialogOpen(true)}
            // spec 011/024: sem mesa operada no ERP nem cliente operado no ERP,
            // não há onde abrir ticket aqui
            disabled={!canCreate}
            title={canCreate ? undefined : 'Todas as mesas são espelhadas do TiFlux — abra o ticket no TiFlux'}
          >
            <PlusIcon />
            Novo ticket
          </Button>
        </div>
      </div>

      {/* Linha 2: mesa + chips de filtro rápido (não valem para a triagem) */}
      <div className={cn('flex flex-wrap items-center gap-2', isPreticketsView && 'hidden')}>
        <div className="relative">
          <NetworkIcon className="pointer-events-none absolute top-1/2 left-2.5 z-10 size-4 -translate-y-1/2 text-white/80" />
          <NativeSelect
            value={search.desk !== undefined ? String(search.desk) : ''}
            onChange={(e) =>
              setSearch({ desk: e.target.value ? Number(e.target.value) : undefined })
            }
            aria-label="Mesa"
            className="h-8 w-56 appearance-none border-transparent bg-[var(--color-sidebar,#03363D)] pr-8 pl-8 text-[13px] font-medium text-white shadow-none transition-opacity hover:opacity-95"
          >
            {search.desk === undefined ? <option value="">Selecione a mesa…</option> : null}
            {desks
              .filter((d) => d.active || d.id === search.desk)
              .map((d) => (
                <option key={d.id} value={d.id}>
                  Mesa: {d.displayName}
                </option>
              ))}
          </NativeSelect>
          <ChevronDownIcon className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-white/80" />
        </div>

        {groups.length > 0 ? (
          <div className="relative">
            <NativeSelect
              value={search.group !== undefined ? String(search.group) : ''}
              onChange={(e) =>
                setSearch({ group: e.target.value ? Number(e.target.value) : undefined })
              }
              aria-label="Grupo de tickets"
              className="h-8 w-44 appearance-none pr-8 text-[13px]"
            >
              <option value="">Grupo: Todos</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  Grupo: {g.name}
                </option>
              ))}
            </NativeSelect>
            <ChevronDownIcon className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        ) : null}

        <div className="mx-1 h-5 w-px bg-border" aria-hidden />

        {STATUS_CHIPS.map(({ value, label, Icon }) => {
          const active = activeStatus === value;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={active}
              onClick={() => {
                if (value !== 'open' && search.view === 'board') {
                  setSearch({ status: value, view: 'list' });
                } else {
                  setSearch({ status: value });
                }
              }}
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-[13px] font-medium transition-colors',
                active
                  ? 'border-transparent bg-foreground text-background'
                  : 'border-border bg-card text-foreground hover:bg-muted/60'
              )}
            >
              <Icon className="size-3.5" />
              {label}
            </button>
          );
        })}

        {/* spec 066: soma com a mesa, a situação, o grupo e a busca */}
        <button
          type="button"
          aria-pressed={search.meus === 1}
          data-tour="tickets-meus"
          onClick={() => setSearch({ meus: search.meus === 1 ? undefined : 1 })}
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-[13px] font-medium transition-colors',
            search.meus === 1
              ? 'border-transparent bg-primary text-primary-foreground'
              : 'border-border bg-card text-foreground hover:bg-muted/60'
          )}
        >
          <UserIcon className="size-3.5" />
          Meus tickets
        </button>
      </div>

      {/* Conteúdo */}
      {isPreticketsView ? (
        // Sem permissão resolvida ainda (ou já negada, com o redirect a
        // caminho) mostra spinner — nunca a lista de tickets piscando.
        canPretickets ? (
          <PreticketsPanel />
        ) : (
          <PageSpinner />
        )
      ) : desksQ.isPending ? (
        <PageSpinner />
      ) : desksQ.isError ? (
        <EmptyData message="Erro ao carregar as mesas" />
      ) : desks.length === 0 ? (
        <EmptyData message="Nenhuma mesa encontrada" />
      ) : search.view === 'board' ? (
        search.desk !== undefined ? (
          <TicketBoard
            deskId={search.desk}
            searchText={search.q}
            group={search.group}
            responsibleId={responsibleId}
            onNewTicket={(stageId) => {
              setDialogStageId(stageId);
              setDialogOpen(true);
            }}
          />
        ) : (
          <PageSpinner />
        )
      ) : (
        <TicketList key={JSON.stringify(listParams)} {...listParams} />
      )}

      {isDialogOpen ? (
        <NewTicketDialog
          desks={desks}
          defaultDeskId={search.desk}
          defaultStageId={dialogStageId}
          onClose={closeDialog}
        />
      ) : null}
    </div>
  );
}

function SegmentButton({
  active,
  onClick,
  Icon,
  label,
  badge = 0,
}: {
  active: boolean;
  onClick: () => void;
  Icon: typeof ListIcon;
  label: string;
  /** Contador ao lado do rótulo (0 = sem badge). */
  badge?: number;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-[5px] px-2.5 text-[13px] font-medium transition-colors',
        active
          ? 'bg-card text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground'
      )}
    >
      <Icon className="size-3.5" />
      {label}
      {badge > 0 ? (
        <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[11px] leading-none font-semibold text-primary-pressed tabular-nums">
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </button>
  );
}
